import ms from 'ms'
import queryString from 'query-string'

import { createApi } from '@shared/api/api-factory'
import { APPLICATION_STATUS } from '@fingerprinting/types'
import { FINGERPRINTING_DOMAIN } from '@fingerprinting/constants/routes'
import { restoreFileFromMetadataFile } from '@fingerprinting/lib/files.utils'
import { APPLICATION_DATE } from '@fingerprinting/features/staff/application/overview/constants'
import { APPLICATION_TYPE_CODE } from '@fingerprinting/features/staff/application/list/constants'
import { attachmentResponseSchema, type TMetadataFile } from '@fingerprinting/types/schemas/file.schema'
import {
  clientListApplicationSchema,
  clientApplicationDetailSchema,
  clientApplicationOverviewSchema,
  clientApplicationProfileSchema,
  applicationDetailChatMessageSchema,
  clientApplicationListDocumentsSchema,
  conversationsResponseSchema,
  workstationSyncHistorySchema,
  type TApplicationDetailChatMessage,
} from '@fingerprinting/types/schemas/application.schema'

import { parseApiResponse } from './utils'

import type { IListApplicationFilterType } from '@fingerprinting/features/client/application/queries'
import type { ApiResponse, PaginatedData, TApiResponseWithRequiredData } from '@/shared/schemas/api-schema'
import type { IChatAttachmentItem } from '@/shared/types/chat'
import type { TClientApplicationDocumentFile, TClientApplicationListDocuments, TClientApplicationProfile } from '@fingerprinting/types/schemas/application.schema'
import type {
  TMailingAddressOption,
  TMailingDestination,
} from '@fingerprinting/features/staff/application/create/constants'

const CLIENT_APPLICATION_BASE_URL = 'intake/applications' as const
const CLIENT_CHAT_BASE_URL = 'intake/chat' as const
const CLIENT_FILES_BASE_URL = 'intake/document-files' as const

const clientApplicationApi = createApi({
  prefixUrl: CLIENT_APPLICATION_BASE_URL,
  domain: FINGERPRINTING_DOMAIN,
})
const clientChatApi = createApi({
  prefixUrl: CLIENT_CHAT_BASE_URL,
  domain: FINGERPRINTING_DOMAIN,
})
const clientFilesApi = createApi({
  prefixUrl: CLIENT_FILES_BASE_URL,
  domain: FINGERPRINTING_DOMAIN,
})

export interface IIntakeInternalDocumentFile {
  id: number
  fileName: string
  filePath: string
  status: string
  uploadedBy: string | null
  uploadedDate: string
  isSigned?: boolean
  commentsCount?: number | null
  fileSize?: number
}

export interface IIntakeInternalDocument {
  id: number
  applicationId: number
  documentTypeId: number
  documentType?: string
  documentTypeName?: string
  documentTypeDescription?: string | null
  visibility: string
  status: string
  createdDate: string
  files: IIntakeInternalDocumentFile[]
}

export interface IIntakeInternalStandaloneFile {
  id: number
  applicationId: number
  applicationDocumentId: number | null
  fileName: string
  filePath: string
  status: string
  uploadedBy: string | null
  uploadedDate: string
  commentsCount: number | null
  hasNewComment: boolean
  fileSize?: number
}

export interface IIntakeInternalDocumentsResponse {
  documents: TClientApplicationListDocuments
  internalFiles: TClientApplicationDocumentFile[]
}

export interface CreateIntakeApplicationPayload {
  email: string
  application: {
    typeCode: string
    typeOther?: string | null
    reason?: string
    apostilleCountryCode?: string | null
    apostilleService?: boolean | null
    mailingDestination: TMailingDestination
    mailingAddressOption?: TMailingAddressOption
    mailingCourierOption?: 'CanadaPostRegistered' | 'PrepaidCourierLabel' | null
    mailingAddress?: {
      country: string
      unitApartment?: string | null
      street: string
      city: string
      stateProvince?: string | null
      zipPostalCode?: string | null
    } | null
    termOfAgreement: boolean
    hearAboutUsFrom?: string | null
  }
  applicantProfile: {
    lastName: string
    firstName: string
    middleName?: string | null
    maidenNameAlias?: string | null
    gender: string
    dateOfBirth: string
    citizenship: string
    unitApartment?: string | null
    streetNumberAndName: string
    city: string
    stateProvinceRegion?: string | null
    zipPostalCode?: string | null
    country: string
    phoneCountryIso: string
    phoneNumber: string
  }
}

export function createIntakeApplication(payload: CreateIntakeApplicationPayload) {
  return clientApplicationApi.post('/', payload)
}

export interface IPostMessageChatApplicationDetailProps {
  message: string
  attachmentIds?: number[]
  documentFileId?: number
  /** Client-only; stripped before API */
  optimisticAttachments?: IChatAttachmentItem[]
}

export interface IPatchMessageChatApplicationDetailProps {
  message: string
}

const clientApplicationRequest = {
  getApplicationOverview: async (
    input?: string | { date?: string; startDate?: string; endDate?: string }
  ) => {
    const args = typeof input === 'string' ? { date: input } : input ?? {}
    const rangeQuery =
      args.startDate && args.endDate
        ? { startDate: args.startDate, endDate: args.endDate }
        : args.date && args.date !== APPLICATION_DATE.alltime
          ? { date: args.date }
          : {}
    const response = await clientApplicationApi.get(
      queryString.stringifyUrl({
        url: '/overview',
        query: rangeQuery,
      })
    )
    return parseApiResponse(response.data, clientApplicationOverviewSchema)
  },
  getListApplications: async ({
    date,
    startDate,
    endDate,
    status,
    page,
    pageNumber,
    typeCode,
    pageSize,
    search,
    sortBy,
    sortDirection,
  }: Partial<IListApplicationFilterType & TPageParams> & { 
    pageNumber?: number
    search?: string
    sortBy?: string
    sortDirection?: 'asc' | 'desc'
    startDate?: string
    endDate?: string
  }) => {
    const pageParam = pageNumber ?? page
    const pageSizeParam = pageSize

    const rangeQuery =
      startDate && endDate
        ? { startDate, endDate }
        : date && date !== APPLICATION_DATE.alltime
          ? { date }
          : {}

    const response = await clientApplicationApi.get(
      queryString.stringifyUrl({
        url: '/',
        query: {
          ...(status !== APPLICATION_STATUS.ALL && { status }),
          ...rangeQuery,
          ...(typeCode !== APPLICATION_TYPE_CODE.ALL && { typeCode }),
          ...(search && { search }),
          ...(sortBy && { sortBy }),
          ...(sortDirection && { sortDirection }),
          page: pageParam,
          pageNumber: pageParam,
          pageSize: pageSizeParam,
        },
      })
    )
    return parseApiResponse(response.data, clientListApplicationSchema)
  },
  getApplicationDetail: async (applicationId: string) => {
    const response = await clientApplicationApi.get(`/${applicationId}`)
    return parseApiResponse(response.data, clientApplicationDetailSchema)
  },
  getListDocuments: async (applicationId: string) => {
    const response: ApiResponse<IIntakeInternalDocumentsResponse> = await clientApplicationApi.get(`/${applicationId}/documents`)
    return parseApiResponse(response.data?.documents, clientApplicationListDocumentsSchema)
  },
  getInternalDocuments: async (applicationId: string) => {
    return clientApplicationApi.get<IIntakeInternalDocumentsResponse>(`/${applicationId}/documents`, {
      visibility: 'InternalOnly',
    })
  },
  createInternalFolder: async (applicationId: string, name: string) => {
    return clientApplicationApi.post(`/${applicationId}/internal-documents/folders`, {
      name,
      visibility: 'InternalOnly',
    })
  },
  uploadInternalFolder: async (applicationId: string, folderName: string, files: File[]) => {
    const formData = new FormData()
    formData.append('folderName', folderName)
    files.forEach((file) => formData.append('files', file))
    return clientApplicationApi.post(`/${applicationId}/internal-documents/upload-folder`, formData, {
      timeout: ms('5m'),
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  uploadInternalFiles: async (applicationId: string, files: File[]) => {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    return clientApplicationApi.post(`/${applicationId}/internal-documents/upload-files`, formData, {
      timeout: ms('5m'),
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  uploadFilesToInternalDocument: async (
    applicationId: string,
    applicationDocumentId: number,
    files: File[]
  ) => {
    const formData = new FormData()
    formData.append('applicationDocumentId', String(applicationDocumentId))
    files.forEach((file) => formData.append('files', file))
    return clientApplicationApi.post(`/${applicationId}/documents/upload`, formData, {
      timeout: ms('5m'),
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  renameInternalDocument: async (applicationId: string, documentId: number, name: string) => {
    return clientApplicationApi.patch(
      `/${applicationId}/internal-documents/${documentId}/rename`,
      { name }
    )
  },
  deleteInternalDocument: async (applicationId: string, documentId: number) => {
    return clientApplicationApi.delete(`/${applicationId}/internal-documents/${documentId}`)
  },
  downloadInternalDocument: async (applicationId: string, documentId: number, fallbackFilename?: string) => {
    const axiosInstance = clientApplicationApi.getAxiosInstance()
    const response = await axiosInstance.get<Blob>(`/${applicationId}/internal-documents/${documentId}/download`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    const disposition = (response.headers['content-disposition'] as string) || ''
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch?.[1] || fallbackFilename || `document-${documentId}.zip`
    return { blob: response.data, filename }
  },
  previewInternalFile: async (applicationId: string, fileId: number) => {
    void applicationId
    const axiosInstance = clientFilesApi.getAxiosInstance()
    const response = await axiosInstance.get<Blob>(`/${fileId}/preview`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    const contentType = (response.headers['content-type'] as string) || 'application/octet-stream'
    return { blob: response.data, contentType }
  },
  downloadInternalFile: async (applicationId: string, fileId: number, fallbackFilename?: string) => {
    void applicationId
    const axiosInstance = clientFilesApi.getAxiosInstance()
    const response = await axiosInstance.get<Blob>(`/${fileId}/download`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })
    const disposition = (response.headers['content-disposition'] as string) || ''
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
    const filename = filenameMatch?.[1] || fallbackFilename || `file-${fileId}`
    return { blob: response.data, filename }
  },
  renameInternalFile: async (applicationId: string, fileId: number, name: string) => {
    return clientApplicationApi.patch(
      `/${applicationId}/internal-documents/files/${fileId}/rename`,
      { name }
    )
  },
  updateApplicantProfile: async (
    applicationId: string,
    data: Partial<TClientApplicationProfile>
  ) => {
    const response = await clientApplicationApi.patch(`/${applicationId}/applicant-profile`, data)
    return parseApiResponse(response.data, clientApplicationProfileSchema)
  },
  uploadFiles: async (
    applicationId: string,
    applicationDocumentId: number,
    listMetadataFile: TMetadataFile[],
    staffDocumentMessageContent?: string
  ) => {
    const formData = new FormData()
    formData.append('ApplicationDocumentId', applicationDocumentId.toString())
    const files = await Promise.all(listMetadataFile.map(restoreFileFromMetadataFile))
    files.forEach((file) => formData.append('Files', file))
    if (staffDocumentMessageContent != null && staffDocumentMessageContent !== '') {
      formData.append('StaffDocumentMessageContent', staffDocumentMessageContent)
    }
    return clientApplicationApi.post(`/${applicationId}/documents/upload`, formData, {
      timeout: ms('5m'),
      // Override default JSON header so the browser can set the multipart boundary
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  submitDocument: async (applicationId: string, documentId: number) => {
    return clientApplicationApi.post(`/${applicationId}/documents/${documentId}/submit`)
  },
  submitAllDocuments: async (applicationId: string) => {
    return clientApplicationApi.post(`/${applicationId}/submit-all`)
  },
  submitPendingApprovals: async (applicationId: string) => {
    return clientFilesApi.post(`/applications/${applicationId}/submit-pending-approvals`)
  },
  reuploadFile: async (fileId: string, newFile: File) => {
    const formData = new FormData()
    formData.append('newFile', newFile)
    const response = await clientFilesApi.put(`/${fileId}/reupload`, formData, {
      timeout: ms('5m'),
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  getMessageChatApplicationDetail: async (
    applicationId: string,
    pageParams: TPageParams & { documentFileId?: number }
  ) => {
    const response = (await clientApplicationApi.get(
      `/${applicationId}/chat/messages`,
      pageParams
    )) as TApiResponseWithRequiredData<PaginatedData<TApplicationDetailChatMessage>>
    const { data } = response
    return data
  },
  postMessageChatApplicationDetail: async (
    applicationId: string,
    data: IPostMessageChatApplicationDetailProps
  ) => {
    const response = await clientApplicationApi.post(`/${applicationId}/chat/messages`, data)
    const { data: parsedData } = parseApiResponse(response.data, applicationDetailChatMessageSchema)
    return parsedData
  },
  patchMessageChatApplicationDetail: async (
    applicationId: string,
    messageId: number,
    data: IPatchMessageChatApplicationDetailProps
  ) => {
    const response = await clientApplicationApi.patch(
      `/${applicationId}/chat/messages/${messageId}`,
      data
    )
    const { data: parsedData } = parseApiResponse(response.data, applicationDetailChatMessageSchema)
    return parsedData
  },
  deleteMessageChatApplicationDetail: async (applicationId: string, messageId: number) => {
    return clientApplicationApi.delete(`/${applicationId}/chat/messages/${messageId}`)
  },
  markChatAsRead: async (applicationId: string) => {
    return clientApplicationApi.post(`/${applicationId}/chat/read`)
  },
  triggerWorkstationSync: async (
    applicationId: string,
    { oriCode, workstationName }: { oriCode: string; workstationName: string }
  ) => {
    return clientApplicationApi.post(
      queryString.stringifyUrl({
        url: `/${applicationId}/workstation-sync`,
        query: { oriCode, workstationName },
      })
    )
  },
  updateApplication: async (applicationId: string, data: unknown) => {
    const response = await clientApplicationApi.patch(`/${applicationId}`, data)
    return parseApiResponse(response.data, clientApplicationDetailSchema)
  },
  updateApplicationNotes: async (applicationId: string, notes: string) => {
    return clientApplicationApi.patch(`/${applicationId}/notes`, {
      notes,
    })
  },
  updateInvoice: async (
    applicationId: string,
    data: { amount: number; status: string; files?: File[]; fileIdsToRemove?: string[] }
  ) => {
    const formData = new FormData()
    formData.append('amount', data.amount.toString())
    formData.append('status', data.status)
    
    if (data.files && data.files.length > 0) {
      data.files.forEach((file) => formData.append('invoiceFiles', file))
    }

    if (data.fileIdsToRemove && data.fileIdsToRemove.length > 0) {
      data.fileIdsToRemove.forEach((id) => formData.append('fileIdsToRemove', id))
    }
    
    const response = await clientApplicationApi.post(`/${applicationId}/invoice`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },
  postAttachmentChatApplicationDetail: async (applicationId: string, data: TMetadataFile) => {
    const formData = new FormData()
    const file = await restoreFileFromMetadataFile(data)
    // Backend expects form-data field name "File"
    formData.append('File', file)
    const response = await clientApplicationApi.post(
      `/${applicationId}/chat/attachments`,
      formData,
      {
        timeout: ms('5m'),
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return parseApiResponse(response.data, attachmentResponseSchema)
  },
  deleteAttachmentChatApplicationDetail: async (applicationId: string, attachmentId: number) => {
    return clientApplicationApi.delete(`/${applicationId}/chat/attachments/${attachmentId}`)
  },
  markFileCommentsAsRead: async (applicationId: string, documentFileId: number) => {
    return clientApplicationApi.post(`/${applicationId}/chat/files/${documentFileId}/mark-comments-read`)
  },
  getConversations: async ({ page, pageSize, search }: { page?: number; pageSize?: number; search?: string } = {}) => {
    const response = await clientChatApi.get('/conversations', {
      ...(page && { page }),
      ...(pageSize && { pageSize }),
      ...(search && { search }),
    })
    return parseApiResponse(response.data, conversationsResponseSchema)
  },
  getChatUnreadCount: async () => {
    const response = await clientChatApi.get('/unread-count')
    return response as TApiResponseWithRequiredData<{ unreadCount: number }>
  },
  deleteApplication: async (applicationId: string) => {
    return clientApplicationApi.delete(`/${applicationId}`)
  },
  bulkDeleteApplications: async (applicationIds: number[]) => {
    return clientApplicationApi.post('/bulk-delete', { ids: applicationIds })
  },
  getWorkstationSyncHistory: async (applicationId: string) => {
    const response = await clientApplicationApi.get(`/${applicationId}/workstation-sync/history`)
    return parseApiResponse(response.data, workstationSyncHistorySchema)
  },
  updateApplicationStatus: async (applicationId: string, status: string) => {
    return clientApplicationApi.put(`/${applicationId}/status`, { status })
  },
}

export default clientApplicationRequest
